import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, Pressable, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import Toast from 'react-native-toast-message';

import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { saveEventRegistrationExtensions } from '@/data/events';
import SheetModal from '@/components/General/SheetModal';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Callout from '@/components/General/Callout';

const formatInstanceDate = (startTime) => {
    if (!startTime) return '';
    const dt = DateTime.fromISO(startTime);
    if (!dt.isValid) return '';
    return dt.toFormat('ccc, MMM d · h:mm a');
};

const instanceName = (inst) => inst?.nameOverride || inst?.Event?.name || 'Untitled Event';

const ExtendRegistrationModal = ({ isOpen, onClose, eventInstanceID, siblingInstances = [], currentExtensions = [] }) => {
    const queryClient = useQueryClient();
    const [selectedIDs, setSelectedIDs] = useState(new Set());

    const currentExtensionIDs = useMemo(
        () => new Set((currentExtensions || []).map(e => e.extendedEventInstanceID)),
        [currentExtensions]
    );

    const candidates = useMemo(() => {
        const now = Date.now();
        return siblingInstances.filter(inst => {
            if (inst.id === eventInstanceID) return false;
            if (inst.isDeleted) return false;
            if (inst.canceled) return false;
            if (inst.registrationEnabled) return false;
            const start = safeNewDate(inst.startTime);
            if (!start || start.getTime() <= now) return false;
            return true;
        });
    }, [siblingInstances, eventInstanceID]);

    useEffect(() => {
        if (isOpen) {
            setSelectedIDs(new Set(currentExtensionIDs));
        }
    }, [isOpen]);

    const toggleSelection = (id) => {
        setSelectedIDs(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const saveMutation = useMutation({
        mutationFn: () => saveEventRegistrationExtensions({
            mainEventInstanceID: eventInstanceID,
            extendedEventInstanceIDs: [...selectedIDs],
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.registrationExtensions(eventInstanceID) });
            const allAffected = new Set([...selectedIDs, ...currentExtensionIDs]);
            allAffected.forEach(id => {
                queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.registrationExtensionSource(id) });
            });
            Toast.show({ type: 'success', text2: 'Registration extensions saved.', position: 'top' });
            onClose?.();
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to save extensions.', position: 'top' });
        },
    });

    const hasChanges = useMemo(() => {
        if (selectedIDs.size !== currentExtensionIDs.size) return true;
        for (const id of selectedIDs) {
            if (!currentExtensionIDs.has(id)) return true;
        }
        return false;
    }, [selectedIDs, currentExtensionIDs]);

    return (
        <SheetModal
            isOpen={isOpen}
            onCancel={onClose}
            onClose={onClose}
            title="Extend Registration"
            subtitle="Check-ins for this event will also count toward selected instances."
            cancelText="Close"
        >
            <View style={styles.container}>
                <Callout
                    type="info"
                    message="If someone's registration covers multiple occurrences of this event (like a multi-day revival) and you still want to track attendance for each occurrence, enable those here. Scanning a user's QR code or tapping 'Check In' on their registration will automatically check them into the nearest upcoming occurrence."
                />
                {candidates.length === 0 ? (
                    <EmptyListPlaceholder message="No upcoming sibling instances available to extend registration to." />
                ) : (
                    <ScrollView style={styles.list} contentContainerStyle={styles.listContent} keyboardShouldPersistTaps="handled">
                        {candidates.map(inst => {
                            const isSelected = selectedIDs.has(inst.id);
                            return (
                                <Pressable
                                    key={inst.id}
                                    style={[styles.row, isSelected && styles.rowSelected]}
                                    onPress={() => toggleSelection(inst.id)}
                                    testID={`extend-registration-row-${inst.id}`}
                                >
                                    <View style={styles.rowMain}>
                                        <Text style={styles.rowName} numberOfLines={1}>{instanceName(inst)}</Text>
                                        <Text style={styles.rowDate}>{formatInstanceDate(inst.startTime)}</Text>
                                    </View>
                                    <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
                                        {isSelected && <Unicon name="checkmark" size={14} colorVariant="onPrimary" />}
                                    </View>
                                </Pressable>
                            );
                        })}
                    </ScrollView>
                )}

                <SolidButton
                    text="Save"
                    type="primary"
                    size="md"
                    disabled={!hasChanges || saveMutation.isPending}
                    processing={saveMutation.isPending}
                    onPress={() => saveMutation.mutate()}
                    testID="extend-registration-save-button"
                />
            </View>
        </SheetModal>
    );
};

const safeNewDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        padding: 16,
        gap: 12,
    },
    list: {
        maxHeight: 420,
    },
    listContent: {
        gap: 8,
        paddingBottom: 8,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 14,
    },
    rowSelected: {
        backgroundColor: colors.primaryContainer,
    },
    rowMain: {
        flex: 1,
        gap: 2,
    },
    rowName: {
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    rowDate: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    checkbox: {
        width: 24,
        height: 24,
        borderRadius: 12,
        borderWidth: 2,
        borderColor: colors.border,
        justifyContent: 'center',
        alignItems: 'center',
    },
    checkboxSelected: {
        backgroundColor: colors.primary,
        borderColor: colors.primary,
    },
}));

export default ExtendRegistrationModal;
